import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { CssTextField } from "../../components/TextField";
import { Add, Remove } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ImgUploaderInput from "../../components/ImgUploaderInput";
import PlusOrMinusButton from "../../components/PlusOrMinusButton";

function RoomSettings({
  setPage,
  roomTypes,
  setRoomTypes,
  colors,
  isRegistration,
}) {
  const { highlights, facilities } = useSelector(
    ({ admin: { highlights, facilities } }) => ({ highlights, facilities })
  );

  // Add a new room type
  const handleNewFieldBtn = () => {
    const newRoomType = {
      name: "",
      description: "",
      no_of_beds: 1,
      base_adults: 1,
      max_adults: 1,
      max_occupancy: 2,
      max_children: 0,
      flexible_occupancy: true,
      ota_rooms: 0,
      is_private: false,
      display_room_no: localStorage?.getItem("isDependent") === "true" ? 1 : 0,
      highlights: [],
      facilities: [],
      images: [],
      rates: {
        standard_rate: null,
        child_rate: 0,
        single_occupancy_rate: 0,
        max_occupancy_rate: null,
        extra_bed_rate: 0,
      },
      rooms: [
        {
          room_no: "101",
          description: "",
          room_size: "",
        },
      ],
    };
    setRoomTypes({ arr: [...roomTypes.arr, newRoomType] });
  };

  // Update a field value for a room type
  const handleNewFieldValueChange = (roomTypeIndex, newValue, keyName) => {
    const updatedRoomTypes = [...roomTypes.arr];
    updatedRoomTypes[roomTypeIndex][keyName] = newValue?.target
      ? newValue.target.value
      : newValue;
    setRoomTypes({ arr: updatedRoomTypes });
  };

  // Update a rate value for a room type
  const handleRateChange = (roomTypeIndex, newValue, keyName) => {
    const updatedRoomTypes = [...roomTypes.arr];
    updatedRoomTypes[roomTypeIndex].rates[keyName] = newValue?.target
      ? newValue.target.value || 0
      : newValue || 0;
    setRoomTypes({ arr: updatedRoomTypes });
  };

  // Remove a room type
  const removeField = (roomTypeIndex) => {
    if (roomTypes?.arr?.length > 1) {
      const filteredRoomTypes = roomTypes.arr.filter(
        (_, idx) => idx !== roomTypeIndex
      );
      setRoomTypes({ arr: filteredRoomTypes });
    }
  };

  // --- Validation Helpers ---
  function getOccupancyError(roomType, field) {
    const { base_adults, max_adults, max_occupancy, max_children } = roomType;
    if (field === "base_adults" && Number(base_adults) < 1) {
      return "Base adults must be at least 1";
    }
    if (
      field === "max_adults" &&
      (Number(max_adults) < Number(base_adults) || Number(max_adults) < 1)
    ) {
      return "Max adults must be at least base adults";
    }
    if (field === "max_occupancy") {
      const adults = Number(max_adults);
      const children = Number(max_children);
      const occupancy = Number(max_occupancy);
      const total = adults + children;

      if (occupancy < adults || occupancy > total) {
        return `Max occupancy must be between max adults and the sum of max adults and max children (${adults} ≤ occupancy ≤ ${total})`;
      }
    }
    return "";
  }

  function getRateError(roomType, field) {
    const { rates, base_adults, max_adults, max_children } = roomType;
    const single = Number(rates.single_occupancy_rate);
    const standard = Number(rates.standard_rate);
    const extra_bed_rate = Number(rates.extra_bed_rate);
    const child = Number(rates.child_rate);

    if (field === "single_occupancy_rate") {
      if (!single || single <= 0) return "Single occupancy rate is required";
    }
    if (field === "standard_rate" && max_adults > 1) {
      if (
        standard === null ||
        standard === undefined ||
        standard === "" ||
        Number.isNaN(standard)
      )
        return "Double occupancy rate is required";
      if (standard < single)
        return "Double occupancy rate must be at least single occupancy rate";
    }

    if (field === "child_rate" && Number(max_children) > 0) {
      if (!child || child <= 0) return "Child rate is required";
    }
    // FIXED LOGIC: Extra bed rate required if max_adults > 2
    if (field === "extra_bed_rate" && Number(max_adults) > 2) {
      if (!extra_bed_rate || extra_bed_rate <= 0)
        return "Extra bed rate is required";
    }
    return "";
  }

  // Form submit handler
  const handleSubmit = async (event) => {
    event.preventDefault();
    setPage(5);
  };

  // Render
  return (
    <Box>
      <form onSubmit={handleSubmit}>
        {roomTypes.arr.map((roomType, roomTypeIndex) => (
          <Box
            key={roomTypeIndex}
            p={2}
            mb={1}
            bgcolor={colors.primary[500]}
            borderRadius={2}
          >
            <Grid container spacing={2}>
              {/* Room Type Header */}
              <Grid item md={12} xs={12}>
                <Typography
                  variant="h4"
                  textTransform="uppercase"
                  color={colors.greenAccent[200]}
                  fontWeight={700}
                >
                  Room Type {roomTypeIndex + 1}
                </Typography>
              </Grid>
              {/* Name */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Name of room type:*</Typography>
                  <CssTextField
                    fullWidth
                    required
                    size="small"
                    type="text"
                    placeholder="Name of room type"
                    value={roomType.name}
                    onChange={(newValue) =>
                      handleNewFieldValueChange(roomTypeIndex, newValue, "name")
                    }
                  />
                </Stack>
              </Grid>
              {/* Number of rooms */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Number of rooms:*</Typography>
                  <PlusOrMinusButton
                    min={1}
                    max={200}
                    size="small"
                    value={roomType.rooms.length}
                    handleChange={(newValue) => {
                      const num = newValue;
                      const arr = Array.from({ length: num }, (_, j) => ({
                        room_no: `10${j + 1}`,
                        description: "",
                        room_size: "",
                      }));
                      handleNewFieldValueChange(roomTypeIndex, arr, "rooms");
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        num,
                        "ota_rooms"
                      );
                    }}
                  />
                </Stack>
              </Grid>
              {/* Occupancy */}
              <Grid item md={6} xs={12}>
                {/* Base Adults */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography width="60%">Base adults:*</Typography>
                  <PlusOrMinusButton
                    min={1}
                    max={50}
                    size="small"
                    value={roomType.base_adults}
                    handleChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "base_adults"
                      )
                    }
                  />
                </Stack>
                {getOccupancyError(roomType, "base_adults") && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {getOccupancyError(roomType, "base_adults")}
                  </Typography>
                )}

                {/* Max Adults */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography width="60%">Max adults:*</Typography>
                  <PlusOrMinusButton
                    min={1}
                    max={50}
                    size="small"
                    value={roomType.max_adults}
                    handleChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "max_adults"
                      )
                    }
                  />
                </Stack>
                {getOccupancyError(roomType, "max_adults") && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {getOccupancyError(roomType, "max_adults")}
                  </Typography>
                )}

                {/* Max Occupancy */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography width="60%">Max occupancy:*</Typography>
                  <PlusOrMinusButton
                    min={1}
                    max={50}
                    size="small"
                    value={roomType.max_occupancy}
                    handleChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "max_occupancy"
                      )
                    }
                  />
                </Stack>
                {getOccupancyError(roomType, "max_occupancy") && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {getOccupancyError(roomType, "max_occupancy")}
                  </Typography>
                )}

                {/* Max Children */}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width="60%">Max children:*</Typography>
                  <PlusOrMinusButton
                    min={0}
                    max={50}
                    size="small"
                    value={roomType.max_children}
                    handleChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "max_children"
                      )
                    }
                  />
                </Stack>
              </Grid>
              {/* Highlights */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Highlights:</Typography>
                  <CssTextField
                    fullWidth
                    select
                    size="small"
                    value={roomType.highlights}
                    SelectProps={{ multiple: true }}
                    onChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "highlights"
                      )
                    }
                  >
                    {highlights?.list?.map((highlight, idx) => (
                      <MenuItem value={highlight._id} key={idx}>
                        <Checkbox
                          sx={{ p: "0 5px" }}
                          size="small"
                          checked={
                            roomType.highlights?.indexOf(highlight._id) > -1
                          }
                        />
                        {highlight.name}
                      </MenuItem>
                    ))}
                  </CssTextField>
                </Stack>
              </Grid>
              {/* Facilities */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Facilities:</Typography>
                  <CssTextField
                    fullWidth
                    select
                    size="small"
                    value={roomType.facilities}
                    SelectProps={{ multiple: true }}
                    onChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "facilities"
                      )
                    }
                  >
                    {facilities?.list?.map((facility, idx) => (
                      <MenuItem value={facility._id} key={idx}>
                        <Checkbox
                          sx={{ p: "0 5px" }}
                          size="small"
                          checked={
                            roomType.facilities?.indexOf(facility._id) > -1
                          }
                        />
                        {facility.name}
                      </MenuItem>
                    ))}
                  </CssTextField>
                </Stack>
              </Grid>
              {/* Description */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Description:</Typography>
                  <CssTextField
                    fullWidth
                    multiline
                    maxRows={Infinity}
                    size="small"
                    type="text"
                    placeholder="Description"
                    value={roomType.description}
                    onChange={(newValue) =>
                      handleNewFieldValueChange(
                        roomTypeIndex,
                        newValue,
                        "description"
                      )
                    }
                  />
                </Stack>
              </Grid>
              {/* Private and Display Room No (if dependent) */}
              {localStorage.getItem("isDependent") === "true" && (
                <>
                  <Grid item md={12} xs={12}>
                    <hr
                      style={{
                        padding: 0,
                        border: 0,
                        margin: 0,
                        borderTop: "1px solid grey",
                      }}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"34%"}>Private:</Typography>
                      <Switch
                        checked={roomType.is_private}
                        onChange={(e) =>
                          handleNewFieldValueChange(
                            roomTypeIndex,
                            e.target.checked,
                            "is_private"
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography width={"60%"}>Number of rooms:*</Typography>
                      <PlusOrMinusButton
                        min={1}
                        max={200}
                        size="small"
                        value={roomType.display_room_no}
                        handleChange={(newValue) =>
                          handleNewFieldValueChange(
                            roomTypeIndex,
                            newValue,
                            "display_room_no"
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                </>
              )}
              {/* Rates Section */}
              <Grid item md={12} xs={12}>
                <Typography
                  variant="h4"
                  color={colors.greenAccent[500]}
                  fontWeight={700}
                >
                  Rates
                </Typography>
              </Grid>
              <Grid item md={6} xs={12}>
                <Stack spacing={2}>
                  {/* Single occupancy rate */}
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography width={"60%"}>
                      Single occupancy rate:*
                    </Typography>
                    <CssTextField
                      fullWidth
                      required
                      size="small"
                      type="number"
                      placeholder="Single occupancy rate"
                      value={roomType.rates.single_occupancy_rate ?? ""}
                      InputProps={{ inputProps: { min: 1 } }}
                      onChange={(newValue) =>
                        handleRateChange(
                          roomTypeIndex,
                          newValue,
                          "single_occupancy_rate"
                        )
                      }
                    />
                  </Stack>
                  {getRateError(roomType, "single_occupancy_rate") && (
                    <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                      {getRateError(roomType, "single_occupancy_rate")}
                    </Typography>
                  )}
                  {/* Double occupancy rate */}
                  {roomType.max_adults > 1 && (
                    <>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography width={"60%"}>
                         Double / Base occupancy rate :*
                        </Typography>
                        <CssTextField
                          fullWidth
                          required
                          size="small"
                          type="number"
                          placeholder="Double occupancy rate"
                          value={roomType.rates.standard_rate ?? ""}
                          onChange={(newValue) =>
                            handleRateChange(
                              roomTypeIndex,
                              newValue,
                              "standard_rate"
                            )
                          }
                          inputProps={{
                            min: roomType.rates.single_occupancy_rate,
                          }}
                        />
                      </Stack>
                      {getRateError(roomType, "standard_rate") && (
                        <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                          {getRateError(roomType, "standard_rate")}
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>
              </Grid>
              {/* Child rate and extra bed rate */}
              <Grid item md={6} xs={12}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Child rate:</Typography>
                  <CssTextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Child rate"
                    value={roomType.rates.child_rate}
                    InputProps={{ inputProps: { min: 0 } }}
                    onChange={(newValue) =>
                      handleRateChange(roomTypeIndex, newValue, "child_rate")
                    }
                  />
                </Stack>
                {getRateError(roomType, "child_rate") && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {getRateError(roomType, "child_rate")}
                  </Typography>
                )}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography width={"60%"}>Extra bed rate:</Typography>
                  <CssTextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Extra bed rate"
                    value={roomType.rates.extra_bed_rate}
                    InputProps={{ inputProps: { min: 0 } }}
                    onChange={(newValue) =>
                      handleRateChange(
                        roomTypeIndex,
                        newValue,
                        "extra_bed_rate"
                      )
                    }
                  />
                </Stack>
                 {getRateError(roomType, "extra_bed_rate") && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {getRateError(roomType, "extra_bed_rate")}
                  </Typography>
                )}
              </Grid>
              {/* Images */}
              <Grid item md={12} xs={12}>
                <Typography
                  variant="h4"
                  color={colors.greenAccent[500]}
                  fontWeight={700}
                >
                  Images
                </Typography>
              </Grid>
              <Grid item md={6} xs={12}>
                <ImgUploaderInput
                  images={roomType.images}
                  onChange={(newValue) =>
                    handleNewFieldValueChange(roomTypeIndex, newValue, "images")
                  }
                />
              </Grid>
              {/* Remove room type button */}
              {roomTypes?.arr?.length > 1 && (
                <Grid item lg={12} xs={12}>
                  <Button
                    title="Remove"
                    size="small"
                    onClick={() => removeField(roomTypeIndex)}
                  >
                    <Remove /> <b>Remove</b>
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
        {/* Add new room type button */}
        <Button fullWidth size="small" onClick={handleNewFieldBtn}>
          <Add /> <b>Add new room type</b>
        </Button>
        {/* Navigation buttons */}
        <Stack
          mt={2}
          direction="row"
          justifyContent={isRegistration ? "space-between" : "flex-end"}
        >
          {isRegistration && (
            <Button variant="outlined" onClick={() => setPage(3)}>
              <b>go back</b>
            </Button>
          )}
          <Button variant="contained" type="submit">
            <b>Next</b>
          </Button>
        </Stack>
      </form>
    </Box>
  );
}

export default RoomSettings;
